/**
 * Filter Panel Component for V4 Dashboard
 * Matches the UI of the original advanced activity dashboard exactly
 */

import {
  Autocomplete,
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { sortWith, ascend, toLower } from 'ramda'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { useEffect } from 'react'
import dayjs from 'dayjs'
import { useAppDispatch } from 'src/store/hooks'
import { activeLinkedServiceChanged } from 'src/v3/manage/manage.slice'
import { CalendarIcon, SearchIcon } from 'src/v3/core/icons'
import { FilterList as FilterIcon } from '@mui/icons-material'
import { useFilters } from '../../hooks/use-filters'
import { useDashboardData } from '../../hooks/use-dashboard-data'
import { FILTER_COLORS } from './filter-colors'

interface FilterPanelProps {
  filterState: ReturnType<typeof useFilters>
}

export default function FilterPanel({ filterState }: FilterPanelProps) {
  let { filters, updateFilter, resetFilters } = filterState
  let dispatch = useAppDispatch()

  // Use dashboard data hook for progressive loading
  let { linkedServices, groups, lightweightUsers, isLoading, hasRequiredData } =
    useDashboardData(filters.linkedServiceID, (serviceID: string) =>
      updateFilter('linkedServiceID', serviceID),
    )

  // Transform lightweight users for filter display
  let users = lightweightUsers.map((user) => ({
    id: user.id,
    name: `${user.firstName} ${user.lastName}`,
    primaryEmail: user.primaryEmail,
  }))

  // Custom date range values
  let customStartDate = filters.customStartDate
    ? dayjs(filters.customStartDate)
    : null
  let customEndDate = filters.customEndDate
    ? dayjs(filters.customEndDate)
    : null

  // Real categories from backend type
  let categories = [
    'Airway/Respiratory/Ventilation',
    'Cardiovascular',
    'Trauma',
    'Medical',
    'Pediatrics',
    'Operations',
    'Critical Care',
    'Other',
  ]

  // Get unique license types from lightweight users
  let licenseTypes = sortWith(
    [ascend(toLower)],
    Array.from(
      new Set(
        lightweightUsers
          .flatMap((user) => user.licenseTypes || [])
          .filter((licenseType): licenseType is string => Boolean(licenseType)),
      ),
    ),
  )

  // Handle linked service change
  let handleLinkedServiceChange = (linkedServiceID: string) => {
    updateFilter('linkedServiceID', linkedServiceID)
    // Reset user and group selections when service changes
    updateFilter('selectedUsers', [])
    updateFilter('selectedGroups', [])
  }

  // Handle license type change with mutual exclusivity
  let handleLicenseTypeChange = (value: string[]) => {
    if (value.length > 0 && filters.selectedGroups.length > 0) {
      updateFilter('selectedGroups', [])
    }
    updateFilter('selectedLicenseTypes', value)
  }

  // Handle group change with mutual exclusivity
  let handleGroupChange = (value: string[]) => {
    if (value.length > 0 && filters.selectedLicenseTypes.length > 0) {
      updateFilter('selectedLicenseTypes', [])
    }
    updateFilter('selectedGroups', value)
  }

  // Handle activity type change
  let handleActivityTypeChange = (value: string[]) => {
    updateFilter('selectedActivityTypes', value)
  }

  // Activity type options - include license/credential only when toggle is enabled
  let activityTypeOptions = [
    { value: 'ce-activity', label: 'CE Activity' },
    { value: 'assignment', label: 'Assignment' },
    { value: 'user-action', label: 'User Action' },
    ...(filters.showLicenseCredentialEvents
      ? [
          { value: 'license-activity', label: 'License Activity' },
          { value: 'credential-activity', label: 'Credential Activity' },
        ]
      : []),
  ]

  // Sync linked service to legacy Redux state for quiz analytics compatibility
  useEffect(() => {
    if (filters.linkedServiceID) {
      // Dispatch to legacy Redux state for quiz analytics component compatibility
      dispatch(activeLinkedServiceChanged(filters.linkedServiceID))
    }
  }, [filters.linkedServiceID, dispatch])

  // Render skeleton loading state
  if (isLoading || !hasRequiredData) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Card sx={{ mb: 4, p: 3 }}>
          <Box className="grid grid-cols-1 gap-6 md:grid-cols-3">
            <Skeleton variant="rectangular" height={56} />
            <Skeleton variant="rectangular" height={56} />
            <Skeleton variant="rectangular" height={56} />
          </Box>
          {/* Advanced Filters Skeleton */}
          <Card variant="outlined" sx={{ p: 2, mt: 3 }}>
            <Box
              sx={{
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Skeleton variant="text" width={150} height={24} />
              <Skeleton variant="rectangular" width={100} height={32} />
            </Box>
            <Box className="grid grid-cols-1 gap-4 md:grid-cols-4">
              <Skeleton variant="rectangular" height={56} />
              <Skeleton variant="rectangular" height={56} />
              <Skeleton variant="rectangular" height={56} />
              <Skeleton variant="rectangular" height={56} />
            </Box>
          </Card>
        </Card>
      </LocalizationProvider>
    )
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Card sx={{ mb: 4, p: 3 }}>
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fit, minmax(300px, 1fr))',
            },
          }}
        >
          {/* Linked Service Selection */}
          <Box>
            <FormControl fullWidth>
              <InputLabel>Select Linked Service</InputLabel>
              <Select
                value={filters.linkedServiceID}
                onChange={(e) => handleLinkedServiceChange(e.target.value)}
                label="Select Linked Service"
                startAdornment={<SearchIcon sx={{ mr: 1 }} />}
              >
                {linkedServices.map((service) => (
                  <MenuItem key={service.id} value={service.id}>
                    {service.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Time Range */}
          <Box>
            <FormControl fullWidth>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={filters.timeRange}
                onChange={(e) =>
                  updateFilter('timeRange', e.target.value as any)
                }
                label="Time Range"
                startAdornment={<CalendarIcon sx={{ mr: 1 }} />}
              >
                <MenuItem value="week">Last Week</MenuItem>
                <MenuItem value="month">Last Month</MenuItem>
                <MenuItem value="quarter">Last Quarter</MenuItem>
                <MenuItem value="year">Last Year</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* View Mode */}
          <Box>
            <ToggleButtonGroup
              value={filters.viewMode}
              exclusive
              fullWidth
              onChange={(_, value) => {
                if (value) updateFilter('viewMode', value)
              }}
            >
              <ToggleButton value="summary">Summary</ToggleButton>
              <ToggleButton value="timeline">Timeline</ToggleButton>
              <ToggleButton value="analytics">Analytics</ToggleButton>
              <ToggleButton value="export">Export</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Custom Date Range */}
        {filters.timeRange === 'custom' && (
          <Box className="grid grid-cols-1 gap-6 mt-6 md:grid-cols-2">
            <DatePicker
              label="Start Date"
              value={customStartDate}
              onChange={(newValue) =>
                updateFilter('customStartDate', newValue?.toISOString() || null)
              }
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="End Date"
              value={customEndDate}
              onChange={(newValue) =>
                updateFilter('customEndDate', newValue?.toISOString() || null)
              }
              slotProps={{ textField: { fullWidth: true } }}
            />
          </Box>
        )}

        {/* Advanced Filters */}
        <Card variant="outlined" sx={{ p: 2, mt: 3 }}>
          <Box
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <FilterIcon />
              Advanced Filters
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {filters.viewMode === 'timeline' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={filters.showLicenseCredentialEvents}
                      onChange={(e) =>
                        updateFilter(
                          'showLicenseCredentialEvents',
                          e.target.checked,
                        )
                      }
                      size="small"
                    />
                  }
                  label="License/Credential Events"
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    },
                  }}
                />
              )}
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={resetFilters}
                sx={{ minWidth: 'auto' }}
                disabled={
                  filters.viewMode === 'summary' ||
                  filters.viewMode === 'export'
                }
              >
                Reset Filters
              </Button>
            </Box>
          </Box>
          {filters.viewMode === 'summary' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Summary View - Showing aggregate data across all users and
                groups
              </Typography>
              <Typography variant="caption">
                Advanced filters are disabled for the summary dashboard. Use
                Timeline or Analytics view for detailed filtering.
              </Typography>
            </Alert>
          )}
          {filters.viewMode === 'export' && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Export View - All data available for export
              </Typography>
              <Typography variant="caption">
                Advanced filters are disabled for the export view. Only date
                range and linked service filters apply to exported data.
              </Typography>
            </Alert>
          )}
          <Box className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-5">
            {/* User Filter */}
            <Box>
              <Autocomplete
                multiple
                options={users}
                getOptionLabel={(option) =>
                  `${option.name} (${option.primaryEmail})`
                }
                value={users.filter((user) =>
                  filters.selectedUsers.includes(user.id),
                )}
                onChange={(_, value) =>
                  updateFilter(
                    'selectedUsers',
                    value.map((user) => user.id),
                  )
                }
                disabled={
                  !filters.linkedServiceID ||
                  filters.viewMode === 'summary' ||
                  filters.viewMode === 'export'
                }
                disableCloseOnSelect
                filterOptions={(options, { inputValue }) => {
                  if (!inputValue) return options.slice(0, 100) // Limit initial display to 100 users
                  return options
                    .filter(
                      (option) =>
                        option.name
                          .toLowerCase()
                          .includes(inputValue.toLowerCase()) ||
                        option.primaryEmail
                          .toLowerCase()
                          .includes(inputValue.toLowerCase()),
                    )
                    .slice(0, 100) // Limit search results to 100 users
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Users"
                    placeholder="Search users..."
                    helperText={
                      filters.viewMode === 'summary'
                        ? 'Not available in Summary view'
                        : filters.viewMode === 'export'
                          ? 'Not available in Export view'
                          : `${users.length} users available`
                    }
                  />
                )}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox checked={selected} style={{ marginRight: 8 }} />
                    <Box>
                      <Typography variant="body2">{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.primaryEmail}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderValue={(value, getItemProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.name}
                      size="small"
                      color={FILTER_COLORS.users}
                      {...getItemProps({ index })}
                    />
                  ))
                }
                slotProps={{
                  listbox: {
                    style: { maxHeight: 300 },
                  },
                }}
              />
            </Box>

            {/* Category Filter */}
            <Box>
              <Autocomplete
                multiple
                options={categories}
                value={filters.selectedCategories}
                onChange={(_, value) =>
                  updateFilter('selectedCategories', value)
                }
                disabled={
                  filters.viewMode === 'summary' ||
                  filters.viewMode === 'export'
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Categories"
                    placeholder="Select categories..."
                    helperText={
                      filters.viewMode === 'summary'
                        ? 'Not available in Summary view'
                        : filters.viewMode === 'export'
                          ? 'Not available in Export view'
                          : undefined
                    }
                  />
                )}
                renderValue={(value, getItemProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      color={FILTER_COLORS.categories}
                      {...getItemProps({ index })}
                    />
                  ))
                }
              />
            </Box>

            {/* License Type Filter */}
            <Box>
              <Autocomplete
                multiple
                options={licenseTypes}
                value={filters.selectedLicenseTypes}
                onChange={(_, value) => handleLicenseTypeChange(value)}
                disabled={
                  filters.viewMode === 'summary' ||
                  filters.viewMode === 'export'
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by License Types"
                    placeholder="Select license types..."
                    helperText={
                      filters.viewMode === 'summary'
                        ? 'Not available in Summary view'
                        : filters.viewMode === 'export'
                          ? 'Not available in Export view'
                          : undefined
                    }
                  />
                )}
                renderValue={(value, getItemProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option}
                      size="small"
                      color={FILTER_COLORS.licenseTypes}
                      {...getItemProps({ index })}
                    />
                  ))
                }
              />
            </Box>

            {/* Group Filter */}
            <Box>
              <Autocomplete
                multiple
                options={groups}
                getOptionLabel={(option) => option.name}
                value={groups.filter((group) =>
                  filters.selectedGroups.includes(group.id || ''),
                )}
                onChange={(_, value) =>
                  handleGroupChange(value.map((group) => group.id || ''))
                }
                disabled={
                  filters.viewMode === 'summary' ||
                  filters.viewMode === 'export'
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Groups"
                    placeholder="Select groups..."
                    helperText={
                      filters.viewMode === 'summary'
                        ? 'Not available in Summary view'
                        : filters.viewMode === 'export'
                          ? 'Not available in Export view'
                          : undefined
                    }
                  />
                )}
                renderValue={(value, getItemProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.name}
                      size="small"
                      color={FILTER_COLORS.groups}
                      {...getItemProps({ index })}
                    />
                  ))
                }
              />
            </Box>

            {/* Activity Type Filter */}
            <Box>
              <Autocomplete
                multiple
                options={activityTypeOptions}
                getOptionLabel={(option) => option.label}
                value={activityTypeOptions.filter((option) =>
                  filters.selectedActivityTypes.includes(option.value),
                )}
                onChange={(_, value) =>
                  handleActivityTypeChange(value.map((option) => option.value))
                }
                disabled={
                  filters.viewMode === 'summary' ||
                  filters.viewMode === 'analytics' ||
                  filters.viewMode === 'export'
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by Activity Types"
                    placeholder="Select activity types..."
                    helperText={
                      filters.viewMode === 'summary'
                        ? 'Not available in Summary view'
                        : filters.viewMode === 'analytics'
                          ? 'Not available in Analytics view'
                          : filters.viewMode === 'export'
                            ? 'Not available in Export view'
                            : undefined
                    }
                  />
                )}
                renderValue={(value, getItemProps) =>
                  value.map((option, index) => (
                    <Chip
                      variant="outlined"
                      label={option.label}
                      size="small"
                      color={FILTER_COLORS.activityTypes}
                      {...getItemProps({ index })}
                    />
                  ))
                }
              />
            </Box>
          </Box>
        </Card>
      </Card>
    </LocalizationProvider>
  )
}
